import { ArrowBackOutlined, RestartAltOutlined, SaveOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { EditPage, ResourceState } from '../../../design-system';
import { EmployeeNumberEditor } from '../components/EmployeeNumberEditor';
import { ReferenceDataOverview } from '../components/ReferenceDataOverview';
import { JobFamilyReferenceSettings } from '../components/JobFamilyReferenceSettings';
import { JobProfileReferenceSettings } from '../components/JobProfileReferenceSettings';
import { JobLevelReferenceSettings } from '../components/JobLevelReferenceSettings';
import { JobTypeReferenceSettings } from '../components/JobTypeReferenceSettings';
import { LocationReferenceSettings } from '../components/LocationReferenceSettings';
import { WorkSpaceReferenceSettings } from '../components/WorkSpaceReferenceSettings';
import {
  defaultEmployeeNumberSettings,
  referenceJobFamilyFixtures,
  referenceJobLevelFixtures,
  referenceJobProfileFixtures,
  referenceJobTypeFixtures,
  referenceLocationFixtures,
  referenceWorkSpaceFixtures,
} from '../fixtures/tenantSettingsFixtures';
import { ruleKey } from '../model/employeeNumber';
import { useAsyncResource, type AsyncResource } from '../../../shared/useAsyncResource';
import type { TenantSettingsPageProps, TenantSettingsView } from '../model/types';
import { useEmployeeNumberWorkspace } from '../model/useEmployeeNumberWorkspace';
import { useTenantSettingsRoute } from '../model/useTenantSettingsRoute';
export function ReferenceDataPage({ port, route, fixtureMode, access }: TenantSettingsPageProps) {
  const location = useTenantSettingsRoute('admin-reference-data', route);
  const canReadStaffing = access.scopes.includes('staffing.read');
  const canWriteStaffing = canReadStaffing && !access.readOnly && access.scopes.includes('staffing.write');
  const canWriteSettings =
    access.scopes.includes('tenant.settings.read') &&
    !access.readOnly &&
    access.scopes.includes('tenant.settings.write');
  const employeeNumberWorkspace = useEmployeeNumberWorkspace(port, fixtureMode, canWriteSettings);
  const load = <T,>(fixtures: T[], request: () => Promise<T[]>) =>
    !canReadStaffing ? Promise.resolve([] as T[]) : fixtureMode ? Promise.resolve(fixtures) : request();
  const {
    resource: employee,
    draft,
    dirty,
    valid,
    saving,
    error,
    notice,
    setDraft,
    clearNotice,
    save,
    cancel,
    reset,
  } = employeeNumberWorkspace;
  const locations = useAsyncResource(
    () => load(referenceLocationFixtures, port.listReferenceLocations),
    [canReadStaffing, fixtureMode, port],
  );
  const workSpaces = useAsyncResource(
    () => load(referenceWorkSpaceFixtures, port.listReferenceWorkSpaces),
    [canReadStaffing, fixtureMode, port],
  );
  const jobFamilies = useAsyncResource(
    () => load(referenceJobFamilyFixtures, port.listReferenceJobFamilies),
    [canReadStaffing, fixtureMode, port],
  );
  const jobProfiles = useAsyncResource(
    () => load(referenceJobProfileFixtures, port.listReferenceJobProfiles),
    [canReadStaffing, fixtureMode, port],
  );
  const jobLevels = useAsyncResource(
    () => load(referenceJobLevelFixtures, port.listReferenceJobLevels),
    [canReadStaffing, fixtureMode, port],
  );
  const jobTypes = useAsyncResource(
    () => load(referenceJobTypeFixtures, port.listReferenceJobTypes),
    [canReadStaffing, fixtureMode, port],
  );
  const back = () => location.navigate('reference-data');
  const resource = <T,>(value: AsyncResource<T[]>) => ({
    data: value.data,
    loading: value.loading,
    error: value.error?.message,
    reload: value.reload,
  });
  if (location.view === 'locations')
    return (
      <LocationReferenceSettings
        port={port}
        fixtureMode={fixtureMode}
        canRead={canReadStaffing}
        canWrite={canWriteStaffing}
        onBack={back}
        titleRef={location.titleRef}
        resource={resource(locations)}
      />
    );
  if (location.view === 'work-spaces')
    return (
      <WorkSpaceReferenceSettings
        port={port}
        fixtureMode={fixtureMode}
        canRead={canReadStaffing}
        canWrite={canWriteStaffing}
        onBack={back}
        titleRef={location.titleRef}
        locationResource={resource(locations)}
        workSpaceResource={resource(workSpaces)}
      />
    );
  if (location.view === 'job-families')
    return (
      <JobFamilyReferenceSettings
        port={port}
        fixtureMode={fixtureMode}
        canRead={canReadStaffing}
        canWrite={canWriteStaffing}
        onBack={back}
        titleRef={location.titleRef}
        familyResource={resource(jobFamilies)}
        profileResource={resource(jobProfiles)}
      />
    );
  if (location.view === 'job-profiles')
    return (
      <JobProfileReferenceSettings
        port={port}
        fixtureMode={fixtureMode}
        canRead={canReadStaffing}
        canWrite={canWriteStaffing}
        onBack={back}
        titleRef={location.titleRef}
        familyResource={resource(jobFamilies)}
        profileResource={resource(jobProfiles)}
      />
    );
  if (location.view === 'job-levels')
    return (
      <JobLevelReferenceSettings
        port={port}
        fixtureMode={fixtureMode}
        canRead={canReadStaffing}
        canWrite={canWriteStaffing}
        onBack={back}
        titleRef={location.titleRef}
        resource={resource(jobLevels)}
      />
    );
  if (location.view === 'job-types')
    return (
      <JobTypeReferenceSettings
        port={port}
        fixtureMode={fixtureMode}
        canRead={canReadStaffing}
        canWrite={canWriteStaffing}
        onBack={back}
        titleRef={location.titleRef}
        resource={resource(jobTypes)}
      />
    );
  const employeeView = location.view === 'employee-number';
  const open = (view: string) => location.navigate(view as TenantSettingsView);
  return (
    <EditPage
      title={employeeView ? '사번 발급 정책' : '기준 정보'}
      titleRef={location.titleRef}
      width={employeeView ? 'standard' : 'compact'}
      presentation={employeeView ? 'plain' : 'sectioned'}
      navigation={
        employeeView ? (
          <Tooltip title="기준 정보로 돌아가기">
            <span>
              <IconButton aria-label="기준 정보로 돌아가기" onClick={back} disabled={saving}>
                <ArrowBackOutlined />
              </IconButton>
            </span>
          </Tooltip>
        ) : undefined
      }
      actions={
        employeeView && canWriteSettings ? (
          <Button
            variant="outlined"
            startIcon={<RestartAltOutlined />}
            disabled={saving || ruleKey(draft) === ruleKey(defaultEmployeeNumberSettings)}
            onClick={reset}
          >
            기본값 불러오기
          </Button>
        ) : undefined
      }
      editActions={
        employeeView && canWriteSettings ? (
          <>
            <Button variant="outlined" disabled={!dirty || saving} onClick={cancel}>취소</Button>
            <Button
              variant="contained"
              startIcon={<SaveOutlined />}
              onClick={save}
              disabled={!dirty || !valid || saving}
            >
              {saving ? '저장 중…' : '변경사항 저장'}
            </Button>
          </>
        ) : undefined
      }
    >
      <Box>
        {employeeView ? (
          <ResourceState
            loading={employee.loading}
            error={employee.error?.message}
            empty={false}
            onRetry={employee.reload}
          >
            <EmployeeNumberEditor
              draft={draft}
              canWrite={canWriteSettings}
              saving={saving}
              error={error}
              notice={notice}
              onNoticeClose={clearNotice}
              onChange={(settings) => {
                setDraft(settings);
                clearNotice();
              }}
            />
          </ResourceState>
        ) : (
          <ReferenceDataOverview
            canReadStaffing={canReadStaffing}
            employee={{
              ...employee,
              data: draft,
            }}
            locations={locations}
            workSpaces={workSpaces}
            jobFamilies={jobFamilies}
            jobProfiles={jobProfiles}
            jobLevels={jobLevels}
            jobTypes={jobTypes}
            onOpen={open}
          />
        )}
      </Box>
    </EditPage>
  );
}
